import { useState } from "react";
import { toast } from "sonner";
import { User, Mail, KeyRound } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { PasswordInput } from "@/components/PasswordInput";

const inputCls = "mt-1.5 w-full rounded-xl border border-[#27272A] bg-[#121215] px-4 py-3 text-base text-[#FAF9F6] placeholder:text-[#8A8E96] focus:border-[#D4AF37]/60 focus:outline-none";
const label = "text-sm font-semibold text-[#B4B4BC]";

function Section({ icon: Icon, title, children, onSubmit, busy, cta, testId }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-2xl border border-[#27272A] bg-[#0D0D0F] p-5" data-testid={testId}>
      <p className="flex items-center gap-2 text-lg font-semibold text-[#FAF9F6]"><Icon size={18} className="text-[#D4AF37]" /> {title}</p>
      {children}
      <button type="submit" disabled={busy} data-testid={`${testId}-submit`} className="btn-gold w-full rounded-full px-6 py-3 text-sm uppercase tracking-wide disabled:opacity-50">
        {busy ? "Un momento…" : cta}
      </button>
    </form>
  );
}

function useSubmit(fn, ok) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return [run, busy];
}

function ProfileForm() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [submit, busy] = useSubmit(async () => setUser((await api.put("/me/profile", f)).data), "Dati aggiornati");
  return (
    <Section icon={User} title="Nome e telefono" onSubmit={submit} busy={busy} cta="Salva" testId="account-profile-form">
      <div><label className={label}>Nome</label><input required maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-name-input" className={inputCls} /></div>
      <div><label className={label}>Telefono</label><input type="tel" required pattern="\+?[0-9 ]{6,20}" title="Solo numeri, eventualmente con + iniziale" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="account-phone-input" placeholder="+39 333 1234567" className={inputCls} /></div>
    </Section>
  );
}

function EmailForm() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ new_email: "", current_password: "" });
  const [submit, busy] = useSubmit(async () => {
    setUser((await api.post("/me/change-email", f)).data);
    setF({ new_email: "", current_password: "" });
  }, "Email aggiornata");
  return (
    <Section icon={Mail} title="Email di accesso" onSubmit={submit} busy={busy} cta="Cambia email" testId="account-email-form">
      <p className="text-sm text-[#B4B4BC]">Attuale: <b className="text-[#FAF9F6]" data-testid="account-current-email">{user?.email}</b></p>
      <div><label className={label}>Nuova email</label><input type="email" required value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} data-testid="account-new-email-input" className={inputCls} /></div>
      <div><label className={label}>Password attuale</label><PasswordInput value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} testId="account-email-password-input" placeholder="Per confermare che sei tu" minLength={1} className={inputCls} /></div>
    </Section>
  );
}

function PasswordForm() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [submit, busy] = useSubmit(async () => {
    if (f.new_password !== f.confirm) throw { response: { data: { detail: "Le due password non coincidono" } } };
    await api.post("/me/change-password", { current_password: f.current_password, new_password: f.new_password });
    setF({ current_password: "", new_password: "", confirm: "" });
  }, "Password aggiornata");
  return (
    <Section icon={KeyRound} title="Password" onSubmit={submit} busy={busy} cta="Cambia password" testId="account-password-form">
      <div><label className={label}>Password attuale</label><PasswordInput value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} testId="account-current-password-input" placeholder="La password che usi ora" minLength={1} className={inputCls} /></div>
      <div><label className={label}>Nuova password</label><PasswordInput value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} testId="account-new-password-input" autoComplete="new-password" className={inputCls} /></div>
      <div><label className={label}>Conferma nuova password</label><PasswordInput value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testId="account-confirm-password-input" placeholder="Ripeti la nuova password" autoComplete="new-password" className={inputCls} /></div>
    </Section>
  );
}

export default function Account() {
  return (
    <AppShell title="I miei dati" greeting="Account">
      <div className="space-y-5 px-5 pb-10 pt-6" data-testid="account-page">
        <ProfileForm />
        <EmailForm />
        <PasswordForm />
      </div>
    </AppShell>
  );
}
